import { BulkWords } from "./revision-ui.tsx";
import { StudyNotice } from "./productivity.tsx";
import { useStudy } from "./use-study.ts";
import { Scoreboard } from "./scores.tsx";
import { Overview, Practice, TestList, useGo } from "./study.tsx";
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Attempt, REPORT_TYPES, type Account } from "@ca/shared";
import { TestBundle, type BundleQuestion } from "@ca/content/bundle";
import { Review } from "@ca/content/review";
import { Button, Chip, Fish, Logo, Modal, ErrorState, Select, Switch } from "@ca/ui";
import { useFocusClock } from "./focus.tsx";
import { api, HttpError } from "./api.ts";
import { uuidv7 } from "./queue.ts";
import { attemptSync, type AttemptRecord, type AttemptWrite } from "./attempt-sync.ts";
interface Props {
  account: Account;
  path: string;
  notify: (message: string) => void;
}
const time = (seconds: number) =>
  `${Math.floor(Math.max(0, seconds) / 60)
    .toString()
    .padStart(2, "0")}:${Math.floor(Math.max(0, seconds) % 60)
    .toString()
    .padStart(2, "0")}`;
function Html({ value }: { value: string | null | undefined }) {
  return value ? <div className="rich" dangerouslySetInnerHTML={{ __html: value }} /> : null;
}
export function LearningPage(props: Props) {
  if (props.path.startsWith("/attempts/"))
    return (
      <Player
        key={`${props.account.id}:${props.path}`}
        {...props}
        id={props.path.split("/")[2] ?? ""}
      />
    );
  if (props.path.startsWith("/practice/")) return <Practice key={props.path} {...props} />;
  if (props.path === "/scores") return <Scoreboard {...props} />;
  if (props.path === "/study") return <Overview {...props} />;
  return <TestList {...props} />;
}
function screens(bundle: TestBundle) {
  const out: BundleQuestion[][] = [];
  for (const q of bundle.questions) {
    const last = out.at(-1);
    if (q.passageId && last?.[0]?.passageId === q.passageId) last.push(q);
    else out.push([q]);
  }
  return out;
}
function Player({ account, id, notify }: Props & { id: string }) {
  const go = useGo(),
    [record, setRecord] = useState<AttemptRecord | null>(null),
    [pending, setPending] = useState<AttemptWrite[]>([]),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [screen, setScreen] = useState(0),
    [syncOpen, setSyncOpen] = useState(false),
    [resultOpen, setResultOpen] = useState(false),
    [report, setReport] = useState<BundleQuestion | null>(null),
    [review, setReview] = useState<Review | null>(null),
    [reviewError, setReviewError] = useState(""),
    [evidence, setEvidence] = useState(true),
    [translation, setTranslation] = useState(false),
    [seconds, setSeconds] = useState<number | null>(null),
    [questionSecs, setQuestionSecs] = useState(0),
    [gridOpen, setGridOpen] = useState(false),
    focus = useFocusClock(account.id),
    cache = useQueryClient();
  const player = useRef<HTMLDivElement>(null),
    clock = useRef<{ stamp: string; now: number; perf: number } | null>(null),
    selectedAt = useRef(performance.now()),
    initial = useRef(true),
    lastStatus = useRef("");
  const alive = useRef(true);
  const update = async () => {
    const [r, p] = await Promise.all([
      attemptSync.read(account.id, id),
      attemptSync.writes(account.id, id),
    ]);
    if (!alive.current) return;
    setRecord(r ?? null);
    setPending(p);
  };
  useEffect(() => {
    alive.current = true;
    const initialize = async () => {
      try {
        const cached = await attemptSync.read(account.id, id);
        if (cached) {
          setRecord(cached);
          setScreen(cached.attempt.state.screen);
        }
        const a = Attempt.parse(await api(`/v1/attempts/${id}`, { accountId: account.id }));
        await attemptSync.cache(account.id, a);
        if (!cached?.bundle) {
          const b = TestBundle.parse(
            await api(`/v1/attempts/${id}/content`, { accountId: account.id }),
          );
          await attemptSync.cache(account.id, a, b);
        }
        const r = await attemptSync.read(account.id, id);
        if (alive.current && r) {
          setRecord(r);
          setScreen(r.attempt.state.screen);
          setError("");
        }
        await update();
      } catch (e) {
        const cached = await attemptSync.read(account.id, id);
        if (alive.current && !cached?.bundle)
          setError(e instanceof Error ? e.message : "Chưa tải được nội dung.");
      } finally {
        initial.current = false;
      }
    };
    const synchronize = async () => {
      await attemptSync.drain(account.id, id);
      await update();
      if (!(await attemptSync.writes(account.id, id)).length && navigator.onLine)
        try {
          const a = Attempt.parse(await api(`/v1/attempts/${id}`, { accountId: account.id }));
          await attemptSync.cache(account.id, a);
          await update();
        } catch {
          /* local content remains usable */
        }
    };
    const changed = () => {
      void update();
    };
    const online = () => {
      void synchronize();
    };
    void initialize().then(synchronize);
    window.addEventListener("ca-attempt-sync", changed);
    window.addEventListener("online", online);
    window.addEventListener("offline", changed);
    const timer = setInterval(() => {
      if (!initial.current) void synchronize();
    }, 5000);
    return () => {
      alive.current = false;
      clearInterval(timer);
      window.removeEventListener("ca-attempt-sync", changed);
      window.removeEventListener("online", online);
      window.removeEventListener("offline", changed);
    };
  }, [account.id, id]);
  useEffect(() => {
    if (!record) return;
    const a = record.attempt;
    if (clock.current?.stamp !== a.serverNow)
      clock.current = {
        stamp: a.serverNow,
        now: new Date(a.serverNow).getTime() + Math.max(0, Date.now() - record.receivedAt),
        perf: performance.now(),
      };
    const tick = () => {
      const now =
        (clock.current?.now ?? Date.now()) +
        performance.now() -
        (clock.current?.perf ?? performance.now());
      setSeconds(
        a.pausedAt
          ? a.remainingSec
          : a.deadlineAt
            ? Math.ceil((new Date(a.deadlineAt).getTime() - now) / 1000)
            : null,
      );
      setQuestionSecs(Math.floor((performance.now() - selectedAt.current) / 1000));
    };
    tick();
    const timer = setInterval(tick, 500);
    return () => {
      clearInterval(timer);
    };
  }, [record?.attempt.serverNow, record?.attempt.deadlineAt, record?.attempt.pausedAt]);
  useEffect(() => {
    const status = record?.attempt.status;
    if (status === "submitted" && lastStatus.current !== status) {
      setResultOpen(true);
      // Điểm, trạng thái đề và số đếm ở menu đổi sau khi nộp.
      for (const key of ["scores", "insights", "tests", "today"])
        void cache.invalidateQueries({ queryKey: [key, account.id] });
      void cache.invalidateQueries({ queryKey: ["revision", account.id] });
    }
    lastStatus.current = status ?? "";
  }, [record?.attempt.status]);
  const bundle = record?.bundle,
    attempt = record?.attempt,
    groups = bundle ? screens(bundle) : [],
    index = Math.min(screen, Math.max(0, groups.length - 1)),
    group = groups[index] ?? [],
    passage = bundle?.passages.find((p) => p.id === group[0]?.passageId),
    closed = attempt?.status !== "in_progress",
    paused = !!attempt?.pausedAt,
    blocked = pending.some((p) => p.blocked),
    expired = seconds !== null && seconds <= 0;
  const settleStudy = useStudy(
    account.id,
    id,
    attempt?.kind === "drill" || attempt?.kind === "part" ? "part" : "test",
    !!attempt && !!bundle && !paused && attempt.status !== "abandoned" && !resultOpen && !syncOpen,
  );
  const pendingFocusGroup = group.some(
    (q) => !attempt?.answers.find((a) => a.questionId === q.id)?.chosen,
  );
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("ca-focus-group", {
        detail: { attempt: attempt?.status === "in_progress", pending: pendingFocusGroup },
      }),
    );
    return () => {
      window.dispatchEvent(
        new CustomEvent("ca-focus-group", { detail: { attempt: false, pending: false } }),
      );
    };
  }, [pendingFocusGroup, attempt?.status]);
  const listeningPhase = attempt?.mode === "exam" && seconds !== null && seconds > 4500;
  const sectionLocked =
    attempt?.mode === "exam" &&
    !closed &&
    group.length > 0 &&
    (listeningPhase ? (group[0]?.part ?? 0) >= 5 : (group[0]?.part ?? 0) <= 4);
  useEffect(() => {
    if (
      attempt?.mode === "exam" &&
      attempt.status === "in_progress" &&
      seconds !== null &&
      !listeningPhase &&
      (group[0]?.part ?? 7) <= 4
    ) {
      const firstReading = groups.findIndex((g) => (g[0]?.part ?? 0) >= 5);
      if (firstReading >= 0) setScreen(firstReading);
    }
  }, [attempt?.mode, attempt?.status, listeningPhase, seconds === null, index]);
  const graded =
    group.length > 0 &&
    group.every((q) => attempt?.answers.some((a) => a.questionId === q.id && a.graded));
  useEffect(() => {
    let cancelled = false;
    setReview(null);
    setReviewError("");
    if (attempt && (attempt.status === "submitted" || graded) && navigator.onLine)
      void api(`/v1/attempts/${id}/review?screen=${index}`, { accountId: account.id })
        .then((value) => {
          if (!cancelled) setReview(Review.parse(value));
        })
        .catch((e: unknown) => {
          if (!cancelled)
            setReviewError(e instanceof Error ? e.message : "Chưa tải được lời giải.");
        });
    return () => {
      cancelled = true;
    };
  }, [id, index, attempt?.status, graded, account.id]);
  async function mutate(operation: AttemptWrite["operation"], body: Record<string, unknown>) {
    if (!attempt) return;
    await attemptSync.enqueue(account.id, id, operation, body, attempt.rev);
    await update();
    await attemptSync.drain(account.id, id);
    await update();
  }
  async function answer(q: BundleQuestion, chosen: "A" | "B" | "C" | "D") {
    if (busy || closed || paused || expired || blocked || sectionLocked) return;
    setBusy(true);
    try {
      const prior = attempt.answers.find((a) => a.questionId === q.id);
      await mutate("answers", {
        answers: [
          {
            questionId: q.id,
            chosen,
            timeSpentMs:
              (prior?.timeSpentMs ?? 0) + Math.round(performance.now() - selectedAt.current),
          },
        ],
      });
      const fresh = await attemptSync.read(account.id, id);
      if (
        fresh?.attempt.immediate &&
        !(await attemptSync.writes(account.id, id)).length &&
        group.every((q) => fresh.attempt.answers.some((a) => a.questionId === q.id && a.chosen))
      ) {
        await attemptSync.enqueue(
          account.id,
          id,
          "grade",
          { questionIds: group.map((q) => q.id) },
          fresh.attempt.rev,
        );
        await attemptSync.drain(account.id, id);
        await update();
      }
      selectedAt.current = performance.now();
    } catch (e) {
      notify(String(e));
      await update();
    } finally {
      setBusy(false);
    }
  }
  async function navigateScreen(next: number) {
    setScreen(next);
    selectedAt.current = performance.now();
    setQuestionSecs(0);
    if (!attempt || closed || paused || expired || blocked || busy) return;
    setBusy(true);
    try {
      await mutate("state", { state: { ...attempt.state, screen: next } });
    } catch (e) {
      notify(String(e));
    } finally {
      setBusy(false);
    }
  }
  async function perform(operation: AttemptWrite["operation"], body: Record<string, unknown>) {
    if (busy) return;
    setBusy(true);
    try {
      if (operation === "submit" || (operation === "state" && body.action === "pause"))
        await settleStudy();
      await attemptSync.drain(account.id, id);
      const writes = await attemptSync.writes(account.id, id);
      if (writes.length) {
        setSyncOpen(true);
        return;
      }
      const current = await attemptSync.read(account.id, id);
      if (!current) return;
      await attemptSync.enqueue(account.id, id, operation, body, current.attempt.rev);
      await attemptSync.drain(account.id, id);
      await update();
    } catch (e) {
      notify(String(e));
    } finally {
      setBusy(false);
    }
  }
  async function flag(q: BundleQuestion) {
    if (!attempt || busy) return;
    const flagged = attempt.state.flagged.includes(q.id)
      ? attempt.state.flagged.filter((x) => x !== q.id)
      : [...attempt.state.flagged, q.id];
    await perform("state", { state: { screen: index, flagged } });
  }
  if (error)
    return (
      <div className="stack">
        <ErrorState
          message={error}
          onRetry={() => {
            window.location.reload();
          }}
        />
      </div>
    );
  if (!attempt || !bundle)
    return (
      <div className="card faint" role="status">
        Đang tải bài làm…
      </div>
    );
  const answered = attempt.answers.filter((a) => a.chosen).length;
  const focusRun = focus.data?.run;
  const focusResting =
    !!focusRun?.completedAt && focus.now < Date.parse(focusRun.breakEndsAt ?? "");
  const focusActive = !!focusRun && (!focusRun.completedAt || focusResting);
  const focusLeft = focusRun
    ? Math.max(
        0,
        Math.ceil(
          (Date.parse(focusResting ? (focusRun.breakEndsAt ?? "") : focusRun.focusEndsAt) -
            focus.now) /
            1000,
        ),
      )
    : 0;
  const syncLabel = busy
    ? "◌ Đang lưu…"
    : pending.length
      ? blocked
        ? "! Cần xử lý · bản trên máy còn nguyên"
        : "◌ Đã lưu trên máy"
      : "✓ Đáp án đã đồng bộ";
  return (
    <div className="player" ref={player}>
      <div className="p-top">
        <Button
          variant="soft"
          size="sm"
          onClick={() => {
            go("/study");
          }}
        >
          ← Thoát
        </Button>
        <span className="p-title">
          <Logo size={34} />
          <h1 style={{ fontSize: "inherit", fontWeight: "inherit", letterSpacing: "inherit" }}>
            {attempt.name}
            {attempt.name.includes(`Part ${group[0]?.part ?? ""}`)
              ? ""
              : ` · Part ${group[0]?.part ?? ""}`}
            {attempt.mode === "exam" ? " · Thi" : ""}
          </h1>
        </span>
        <StudyNotice />
        <span className="spacer" />
        <span className="timerbar" role="timer" data-tip="Đồng hồ làm bài">
          ⏱{" "}
          <span>
            {closed
              ? "Đã kết thúc"
              : paused
                ? time(seconds ?? 0)
                : seconds !== null
                  ? time(attempt.mode === "exam" && listeningPhase ? seconds - 4500 : seconds)
                  : time(questionSecs)}
          </span>
          <small>
            {closed
              ? ""
              : paused
                ? "Tạm dừng"
                : attempt.mode === "exam"
                  ? listeningPhase
                    ? "Nghe"
                    : "Đọc"
                  : seconds !== null
                    ? "Luyện tập"
                    : "cụm này"}
          </small>
          {!closed && attempt.mode === "practice" && attempt.deadlineAt && !paused && (
            <button
              aria-label="Tạm dừng"
              disabled={busy || pending.length > 0 || expired}
              onClick={() => {
                void perform("state", { action: "pause" });
              }}
            >
              ⏸
            </button>
          )}
          {!closed && paused && (
            <button
              aria-label="Tiếp tục"
              disabled={busy}
              onClick={() => {
                void perform("state", { action: "resume" });
              }}
            >
              ▶
            </button>
          )}
        </span>
        <button
          className={`pchip${focusActive ? "" : " idle"}`}
          id="pchip2"
          data-tip="Pomodoro|Bấm để mở toàn màn hình"
          onClick={() => window.dispatchEvent(new Event("ca-focus-open"))}
        >
          🍅{" "}
          <span>
            {focusActive ? `${time(focusLeft)}${focusResting ? " nghỉ" : ""}` : "bật 25′"}
          </span>
        </button>
        <Button
          variant="soft"
          size="sm"
          className="sync-btn"
          onClick={() => {
            setSyncOpen(true);
          }}
        >
          {syncLabel}
        </Button>
        <Button
          variant="soft"
          size="sm"
          onClick={() => window.dispatchEvent(new Event("ca-note-open"))}
        >
          📝 Take note
        </Button>
        <Button
          variant="soft"
          size="sm"
          aria-label="Toàn màn hình"
          onClick={() => {
            void (
              document.fullscreenElement
                ? document.exitFullscreen()
                : player.current?.requestFullscreen()
            )?.catch(() => {
              notify("Trình duyệt chưa cho phép toàn màn hình.");
            });
          }}
        >
          ⛶
        </Button>
        {!closed && (
          <Button
            variant="acc"
            size="sm"
            disabled={busy || paused || blocked}
            onClick={() => {
              if (window.confirm("Nộp bài với các đáp án đã đồng bộ?")) void perform("submit", {});
            }}
          >
            ✈ Nộp bài
          </Button>
        )}
      </div>
      {!navigator.onLine && (
        <div className="resume" role="status">
          Mất mạng: đáp án được lưu trên máy. Đồng hồ vẫn chạy; câu gửi sau hạn sẽ không được tính.
          Chuyển máy chỉ có phần đã đồng bộ.
        </div>
      )}
      {sectionLocked && (
        <div className="resume">
          {listeningPhase
            ? "Phần đọc mở sau khi hết 45 phút nghe."
            : "Phần nghe đã kết thúc. Tiếp tục Part 5–7 trong 75 phút đọc."}
        </div>
      )}
      {expired && !closed && (
        <div className="resume">
          Đã hết giờ. Máy chủ sẽ chấm phần đã nhận; đáp án chưa gửi vẫn giữ trên máy.
        </div>
      )}
      {attempt.result && (
        <div className="card tight row between">
          <h2>
            {attempt.result.totalScore !== null
              ? `${attempt.result.totalScore} / 990`
              : `${attempt.result.correct} / ${attempt.result.total} câu đúng`}
          </h2>
          <Button
            variant="soft"
            size="sm"
            onClick={() => {
              setResultOpen(true);
            }}
          >
            Xem kết quả
          </Button>
        </div>
      )}
      <div
        className="p-body"
        style={passage ? undefined : { gridTemplateColumns: "minmax(0,1fr)" }}
      >
        {passage && (
          <section className={`passage${evidence ? "" : " noev"}`}>
            <p className="faint" style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>
              Questions {group[0]?.number}
              {group.length > 1 ? `–${group.at(-1)?.number ?? ""}` : ""} ·{" "}
              {passage.docCount === 1 ? "một văn bản" : `${passage.docCount} văn bản`}
            </p>
            <div className={passage.html ? "doc" : undefined}>
              <Html value={review?.passage?.html ?? passage.html} />
            </div>
            {translation && review?.passage?.translationVi && (
              <p className="muted" style={{ marginTop: 10 }}>
                {review.passage.translationVi}
              </p>
            )}
            {passage.imageKeys.map((key) => (
              <img
                key={key}
                className="q-image"
                src={mediaUrl(key)}
                alt={`Hình câu ${group[0]?.number}`}
              />
            ))}
            {passage.audioKey && (
              <AudioPlayer
                key={passage.id}
                audioKey={passage.audioKey}
                nextKey={
                  bundle.passages.find((p) => p.id === groups[index + 1]?.[0]?.passageId)
                    ?.audioKey ?? null
                }
                exam={attempt.mode === "exam" && !closed}
                paused={paused}
                ended={() => {
                  if (attempt.mode === "exam" && !closed && index < groups.length - 1)
                    void navigateScreen(index + 1);
                }}
              />
            )}
            {review?.passage?.transcript && (
              <details open style={{ marginTop: 12 }}>
                <summary>Lời thoại</summary>
                {review.passage.transcript.map((line, i) => (
                  <div key={i}>
                    <b>{line.speaker}</b>
                    <Html value={line.enHtml} />
                    <p className="muted">{line.vi}</p>
                  </div>
                ))}
              </details>
            )}
            {(review?.passage?.html || review?.passage?.translationVi) && (
              <div className="toggles">
                {review.passage.html && (
                  <div className="toggle-row">
                    🔦 Dẫn chứng trong bài
                    <Switch label="Dẫn chứng" checked={evidence} onChange={setEvidence} />
                  </div>
                )}
                {review.passage.translationVi && (
                  <div className="toggle-row">
                    🌐 Dịch toàn văn
                    <Switch label="Dịch toàn văn" checked={translation} onChange={setTranslation} />
                  </div>
                )}
              </div>
            )}
          </section>
        )}
        <section className="qpanel">
          {group.map((q) => {
            const selected = attempt.answers.find((a) => a.questionId === q.id),
              r = review?.questions.find((r) => r.id === q.id);
            return (
              <div key={q.id} className="stack">
                <div className="row" style={{ gap: 8 }}>
                  <span className="qn" data-note-question={`Câu ${q.number}`}>
                    Q{q.number}
                  </span>
                  <span className="faint" style={{ fontSize: 12, fontWeight: 700 }}>
                    ⏱ {time(selected ? selected.timeSpentMs / 1000 : questionSecs)}
                  </span>
                  <span className="grow" />
                  <button
                    className="btn btn-soft btn-sm btn-icon"
                    aria-label={`Đánh dấu câu ${q.number}`}
                    aria-pressed={attempt.state.flagged.includes(q.id)}
                    disabled={closed || busy || paused || expired || blocked}
                    onClick={() => {
                      void flag(q);
                    }}
                  >
                    {attempt.state.flagged.includes(q.id) ? "★" : "☆"}
                  </button>
                </div>
                <div className="qtext">
                  <Html value={q.prompt} />
                </div>
                {q.options.map((o) => {
                  const right = !!r && r.correct === o.key,
                    wrong = !!r && selected?.chosen === o.key && r.correct !== o.key;
                  return (
                    <button
                      key={o.key}
                      className={`ans${right ? " right" : wrong ? " wrong" : ""}${!r && selected?.chosen === o.key ? " picked" : ""}`}
                      aria-label={`Câu ${q.number} chọn ${o.key}`}
                      aria-pressed={selected?.chosen === o.key}
                      disabled={
                        busy ||
                        closed ||
                        paused ||
                        expired ||
                        blocked ||
                        sectionLocked ||
                        selected?.graded
                      }
                      onClick={() => {
                        void answer(q, o.key);
                      }}
                    >
                      <span className="rd" />({o.key}) <Html value={o.text} />
                    </button>
                  );
                })}
                {r ? (
                  <div className="explain">
                    <ExplanationView question={r} uid={account.id} notify={notify} />
                  </div>
                ) : attempt.immediate ? (
                  <p className="faint" style={{ fontSize: 13, fontWeight: 600 }}>
                    Chế độ “Làm &amp; xem đáp án ngay”: chọn đáp án để xem giải thích.
                  </p>
                ) : null}
              </div>
            );
          })}
          {!closed && attempt.mode === "practice" && !graded && (
            <Button
              variant="acc"
              disabled={
                busy ||
                paused ||
                expired ||
                blocked ||
                group.some((q) => !attempt.answers.find((a) => a.questionId === q.id)?.chosen)
              }
              onClick={() => {
                void perform("grade", { questionIds: group.map((q) => q.id) });
              }}
            >
              Chấm {group.length === 1 ? "câu này" : "cả cụm"}
            </Button>
          )}
          {reviewError && (
            <div className="resume">
              <span>{reviewError}</span>
              {reviewError.includes("Pro") && (
                <Button
                  variant="acc"
                  size="sm"
                  onClick={() => {
                    go("/pro");
                  }}
                >
                  Mở lời giải với Pro
                </Button>
              )}
            </div>
          )}
        </section>
      </div>
      <div className="p-foot">
        <Button
          variant="soft"
          size="sm"
          onClick={() => {
            if (group[0]) setReport(group[0]);
          }}
        >
          ⚠️ Báo lỗi
        </Button>
        <span className="faint" style={{ fontSize: 12.5, fontWeight: 700 }}>
          {answered}/{bundle.questions.length} câu đã chọn
        </span>
        <span className="spacer" />
        <Button
          variant="soft"
          size="icon"
          aria-label="Cụm trước"
          disabled={index === 0 || busy}
          onClick={() => {
            void navigateScreen(index - 1);
          }}
        >
          ‹
        </Button>
        <button
          className="qgrid-btn"
          onClick={() => {
            setGridOpen(true);
          }}
        >
          ▦ {index + 1}/{groups.length}
        </button>
        <Button
          variant="acc"
          size="icon"
          aria-label="Cụm sau"
          disabled={index >= groups.length - 1 || busy}
          onClick={() => {
            void navigateScreen(index + 1);
          }}
        >
          ›
        </Button>
      </div>
      {gridOpen && (
        <Modal
          title="Danh sách câu"
          head={
            <div>
              <h2>Danh sách câu</h2>
              <p className="muted" style={{ fontSize: 13 }}>
                Đen: đang xem · tím: đã chọn · ★: đánh dấu
              </p>
            </div>
          }
          onClose={() => {
            setGridOpen(false);
          }}
        >
          <div className="grid" style={{ gridTemplateColumns: "repeat(8,minmax(0,1fr))", gap: 8 }}>
            {bundle.questions.map((q) => {
              const chosen = attempt.answers.some((a) => a.questionId === q.id && a.chosen),
                cur = group.some((g) => g.id === q.id);
              return (
                <button
                  key={q.id}
                  className={`chip${cur ? " dark" : chosen ? " acc" : ""}`}
                  style={{ height: 40, justifyContent: "center" }}
                  disabled={
                    attempt.mode === "exam" &&
                    !closed &&
                    (listeningPhase ? q.part >= 5 : q.part <= 4)
                  }
                  aria-label={`Đến câu ${q.number}`}
                  onClick={() => {
                    setGridOpen(false);
                    void navigateScreen(groups.findIndex((g) => g.some((x) => x.id === q.id)));
                  }}
                >
                  {q.number}
                  {attempt.state.flagged.includes(q.id) ? "★" : ""}
                </button>
              );
            })}
          </div>
        </Modal>
      )}
      {syncOpen && (
        <SyncModal
          accountId={account.id}
          attempt={attempt}
          writes={pending}
          close={() => {
            setSyncOpen(false);
          }}
          notify={notify}
          refresh={update}
        />
      )}
      {resultOpen && attempt.result && (
        <Modal
          title="Bạn đã hoàn thành!"
          hideTitle
          wide
          onClose={() => {
            setResultOpen(false);
          }}
        >
          <div className="rep-hero">
            <div>
              <Fish pose="party" />
            </div>
            <div>
              <span className="eyebrow" style={{ color: "inherit", opacity: 0.7 }}>
                Bạn đã hoàn thành · {attempt.name}
              </span>
              <h2>
                {attempt.result.totalScore !== null
                  ? `${attempt.result.totalScore} / 990 điểm`
                  : `${attempt.result.correct} / ${attempt.result.total} câu đúng`}
              </h2>
              <p>Đã ghi {Math.floor(attempt.result.activeSecs / 60)} phút học được xác nhận.</p>
            </div>
          </div>
          <div className="kpis" style={{ marginTop: 14 }}>
            {attempt.result.parts.map((p) => (
              <div key={p.part} className="kpi">
                <span className="k-lbl">Part {p.part}</span>
                <span className="k-val">
                  {p.correct}/{p.total}
                </span>
                <span className="k-sub">
                  đúng {p.total ? Math.round((p.correct / p.total) * 100) : 0}%
                </span>
              </div>
            ))}
          </div>
          {attempt.result.activeSecs === 0 && (
            <p className="faint" style={{ fontSize: 12.5, fontWeight: 600, marginTop: 10 }}>
              Giờ học chỉ tính những khoảng hoạt động được máy chủ xác nhận.
            </p>
          )}
          <div className="row" style={{ marginTop: 16 }}>
            <Button
              variant="soft"
              onClick={() => {
                setResultOpen(false);
                setScreen(0);
              }}
            >
              Xem lại lời giải
            </Button>
            <Button
              onClick={() => {
                go("/errors");
              }}
            >
              📕 Trị lỗi ngay
            </Button>
          </div>
        </Modal>
      )}
      {report && (
        <ReportModal
          question={report}
          accountId={account.id}
          attemptId={id}
          notify={notify}
          close={() => {
            setReport(null);
          }}
        />
      )}
    </div>
  );
}
function mediaUrl(key: string) {
  const base: string = (import.meta.env.VITE_MEDIA_ORIGIN as string | undefined) ?? "/media";
  return `${base.replace(/\/$/, "")}/${key.split("/").map(encodeURIComponent).join("/")}`;
}
function AudioPlayer({
  audioKey,
  nextKey,
  exam,
  paused,
  ended,
}: {
  audioKey: string;
  nextKey: string | null;
  exam: boolean;
  paused: boolean;
  ended: () => void;
}) {
  const ref = useRef<HTMLAudioElement>(null),
    [blocked, setBlocked] = useState(false),
    lastTime = useRef(0),
    [started, setStarted] = useState(false);
  useEffect(() => {
    const audio = ref.current;
    if (!audio) return;
    const play = () => {
      void audio
        .play()
        .then(() => {
          setBlocked(false);
        })
        .catch(() => {
          setBlocked(true);
        });
    };
    if (!paused) {
      play();
    } else audio.pause();
    return () => {
      audio.pause();
    };
  }, [audioKey, paused]);
  useEffect(() => {
    if (!nextKey) return;
    const audio = new Audio(mediaUrl(nextKey));
    audio.preload = "auto";
    audio.load();
    return () => {
      audio.src = "";
    };
  }, [nextKey]);
  return (
    <div className="audio-box">
      <p>
        {exam
          ? "🎧 Chế độ thi: nghe một lượt, không tua. Audio kết thúc sẽ sang cụm tiếp."
          : "🎧 Nghe và luyện theo nhịp của bạn."}
      </p>
      <audio
        ref={ref}
        src={mediaUrl(audioKey)}
        preload="auto"
        controls={!exam}
        onTimeUpdate={() => {
          if (ref.current) lastTime.current = ref.current.currentTime;
        }}
        onSeeking={() => {
          const audio = ref.current;
          if (exam && audio && Math.abs(audio.currentTime - lastTime.current) > 0.5)
            audio.currentTime = lastTime.current;
        }}
        onPlay={() => {
          setStarted(true);
        }}
        onEnded={ended}
      />
      {(blocked || (exam && !started)) && (
        <Button
          disabled={paused}
          onClick={() => {
            void ref.current
              ?.play()
              .then(() => {
                setBlocked(false);
              })
              .catch(() => {
                setBlocked(true);
              });
          }}
        >
          Bấm để phát audio
        </Button>
      )}
    </div>
  );
}
function ExplanationView({
  question: q,
  uid,
  notify,
}: {
  question: Review["questions"][number];
  uid: string;
  notify: (text: string) => void;
}) {
  const e = q.explanation;
  return (
    <div className="stack" style={{ gap: 10 }}>
      <div className="row">
        <span className="faint" style={{ fontWeight: 800, fontSize: 12.5 }}>
          Dạng:
        </span>
        <Chip>{e.category}</Chip>
        {q.chosen === q.correct ? (
          <Chip tone="good">✓ Bạn chọn đúng · Đáp án {q.correct}</Chip>
        ) : (
          <Chip tone="bad">
            ✕ {q.chosen ? `Bạn chọn ${q.chosen} · đã vào Sổ tay lỗi sai` : "Chưa chọn"} · Đáp án{" "}
            {q.correct}
          </Chip>
        )}
      </div>
      {(e.spoken || q.translation.question) && (
        <div className="qvi">
          {e.spoken && (
            <>
              {e.spoken.en}
              <br />
              <span className="muted">{e.spoken.vi}</span>
            </>
          )}
          {!e.spoken && <span className="muted">{q.translation.question}</span>}
        </div>
      )}
      <Html value={e.whyHtml} />
      <div className="eyebrow">Phân tích {e.options.length} đáp án</div>
      {e.options.map((o, i) => (
        <div key={o.key} className={`opt-x${o.key === q.correct ? " ok" : ""}`}>
          <span className="L">{o.key}</span>
          <div>
            {o.keyword && <b>{o.keyword}</b>}
            {o.gloss ? ` — ${o.gloss}` : ""}
            {o.spokenEn && <div>{o.spokenEn}</div>}
            {o.spokenVi && <div className="muted">{o.spokenVi}</div>}
            {q.translation.options?.[i] && <div className="muted">{q.translation.options[i]}</div>}
            <Html value={o.verdictHtml} />
          </div>
        </div>
      ))}
      {e.structure && (
        <section>
          <div className="eyebrow">Cấu trúc câu</div>
          <div className="row">
            {e.structure.segments.map((s, i) => (
              <span key={i} className="chip">
                <b>{s.label}</b> {s.text}
                <small>{s.kind}</small>
              </span>
            ))}
          </div>
          <p>{e.structure.summary}</p>
          <Html value={e.structure.noteHtml} />
          <Html value={e.structure.needHtml} />
        </section>
      )}
      {(e.questionKeywords?.length || e.evidence) && (
        <section>
          <div className="eyebrow">Từ khoá – dẫn chứng</div>
          {e.questionKeywords && <p>{e.questionKeywords.join(" · ")}</p>}
          {e.evidence?.quotes.map((quote, i) => (
            <blockquote key={i}>{quote}</blockquote>
          ))}
          <Html value={e.evidence?.citeHtml} />
          <p className="muted">{e.evidence?.citeVi}</p>
        </section>
      )}
      {e.cross && (
        <section>
          <div className="eyebrow">Ghép thông tin giữa văn bản</div>
          <Html value={e.cross.tipHtml} />
          {e.cross.sources.map((source, i) => (
            <div key={i}>
              <b>{source.label}</b>
              <Html value={source.quoteHtml} />
              <p>{source.vi}</p>
            </div>
          ))}
          <Html value={e.cross.combineHtml} />
        </section>
      )}
      {e.trap && (
        <section>
          <div className="eyebrow">Bẫy: {e.trap.name}</div>
          <Html value={e.trap.html} />
        </section>
      )}
      {e.tipsHtml?.map((tip, i) => (
        <section key={i}>
          <div className="eyebrow">Mẹo</div>
          <Html value={tip} />
        </section>
      ))}
      <Html value={e.extraHtml} />
      <Html value={e.paraphraseHtml} />
      {q.vocab.length > 0 && (
        <section>
          <BulkWords uid={uid} notify={notify} words={q.vocab} />
          <table>
            <tbody>
              {q.vocab.map((v, i) => (
                <tr key={i}>
                  <td>{v.term}</td>
                  <td>{v.meaningVi}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      <p className="muted">{[e.tag, e.kindLabel, e.difficultyLabel].filter(Boolean).join(" · ")}</p>
    </div>
  );
}
function SyncModal({
  accountId,
  attempt,
  writes,
  close,
  notify,
  refresh,
}: {
  accountId: string;
  attempt: Attempt;
  writes: AttemptWrite[];
  close: () => void;
  notify: Props["notify"];
  refresh: () => Promise<void>;
}) {
  const [server, setServer] = useState<Attempt | null>(null),
    [busy, setBusy] = useState(false);
  useEffect(() => {
    void api(`/v1/attempts/${attempt.id}`, { accountId })
      .then((v) => {
        setServer(Attempt.parse(v));
      })
      .catch(() => {});
  }, [accountId, attempt.id]);
  async function useServer() {
    if (!server) return;
    setBusy(true);
    try {
      const fresh = Attempt.parse(await api(`/v1/attempts/${attempt.id}`, { accountId }));
      await attemptSync.useServer(accountId, fresh);
      await refresh();
      close();
    } catch (e) {
      notify(String(e));
    } finally {
      setBusy(false);
    }
  }
  async function reapply() {
    if (!server) return;
    setBusy(true);
    try {
      const fresh = Attempt.parse(await api(`/v1/attempts/${attempt.id}`, { accountId }));
      if (fresh.status !== "in_progress" || fresh.pausedAt)
        throw new Error("Bài server đã kết thúc hoặc tạm dừng. Xuất bản trên máy để đối chiếu.");
      const answers = attempt.answers
        .filter((a) => !fresh.answers.some((s) => s.questionId === a.questionId && s.graded))
        .map(({ questionId, chosen, timeSpentMs }) => ({ questionId, chosen, timeSpentMs }));
      await attemptSync.useServer(accountId, fresh);
      if (answers.length)
        await attemptSync.enqueue(accountId, attempt.id, "answers", { answers }, fresh.rev);
      await attemptSync.drain(accountId, attempt.id);
      await refresh();
      close();
    } catch (e) {
      notify(String(e));
    } finally {
      setBusy(false);
    }
  }
  function exportLocal() {
    const url = URL.createObjectURL(
      new Blob(
        [
          JSON.stringify(
            { attemptId: attempt.id, answers: attempt.answers, state: attempt.state, writes },
            null,
            2,
          ),
        ],
        { type: "application/json" },
      ),
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = `bai-lam-${attempt.id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }
  return (
    <Modal title="Đồng bộ bài làm" onClose={close}>
      <p>
        Chuyển máy chỉ khôi phục phần đã đồng bộ. Bản trả lời trên máy vẫn được giữ khi có xung đột.
      </p>
      <p>
        {writes.length
          ? `${writes.length} thao tác chưa được máy chủ xác nhận.`
          : "Tất cả đáp án đã đồng bộ."}
      </p>
      {writes.map((w) => (
        <p key={w.key}>{w.error ?? "Đang chờ gửi."}</p>
      ))}
      {writes.some((w) => w.blocked) && (
        <>
          <h3>Đối chiếu hai bản</h3>
          <div className="grid g2">
            <div>
              <b>Trên máy · rev {attempt.rev}</b>
              <pre>
                {attempt.answers
                  .map((a) => `${a.questionId.slice(-6)}: ${a.chosen ?? "—"}`)
                  .join("\n")}
              </pre>
            </div>
            <div>
              <b>Server · rev {server?.rev ?? "—"}</b>
              <pre>
                {server?.answers
                  .map((a) => `${a.questionId.slice(-6)}: ${a.chosen ?? "—"}`)
                  .join("\n")}
              </pre>
            </div>
          </div>
        </>
      )}
      <div className="row">
        <Button
          disabled={busy}
          onClick={() => {
            void attemptSync.drain(accountId, attempt.id).then(refresh);
          }}
        >
          Gửi lại
        </Button>
        <Button variant="line" onClick={exportLocal}>
          Xuất bản trên máy
        </Button>
        {writes.some((w) => w.blocked) && server && (
          <>
            <Button
              variant="line"
              disabled={busy}
              onClick={() => {
                void useServer();
              }}
            >
              Dùng bản server
            </Button>
            {server.status === "in_progress" && !server.pausedAt && (
              <Button
                variant="soft"
                disabled={busy}
                onClick={() => {
                  void reapply();
                }}
              >
                Gửi lựa chọn của tôi trên bản mới
              </Button>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
const reportNames: Record<(typeof REPORT_TYPES)[number], string> = {
  dapan: "Đáp án",
  noidung: "Nội dung câu",
  giaithich: "Lời giải",
  audio: "Audio",
  anh: "Hình ảnh",
  khac: "Khác",
};
function ReportModal({
  question,
  accountId,
  attemptId,
  notify,
  close,
}: {
  question: BundleQuestion;
  accountId: string;
  attemptId: string;
  notify: Props["notify"];
  close: () => void;
}) {
  const [type, setType] = useState<(typeof REPORT_TYPES)[number]>("dapan"),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false),
    pending = useRef<{ key: string; body: unknown } | null>(null);
  async function send() {
    setBusy(true);
    try {
      pending.current ??= {
        key: uuidv7(),
        body: { attemptId, questionId: question.id, type, message },
      };
      await api("/v1/reports", {
        method: "POST",
        key: pending.current.key,
        body: pending.current.body,
        accountId,
      });
      notify("Đã nhận báo lỗi. Cảm ơn bạn!");
      close();
    } catch (e) {
      if (e instanceof HttpError) pending.current = null;
      notify(String(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal title={`Báo lỗi · Part ${question.part} câu ${question.number}`} onClose={close}>
      <fieldset
        disabled={busy || !!pending.current}
        className="stack"
        style={{ border: 0, padding: 0, margin: 0 }}
      >
        <div className="field">
          <label>Nhóm lỗi</label>
          <Select
            label="Nhóm lỗi"
            value={type}
            options={REPORT_TYPES.map((t) => ({ value: t, label: reportNames[t] }))}
            onChange={setType}
          />
        </div>
        <div className="field">
          <label htmlFor="report-msg">Mô tả</label>
          <textarea
            id="report-msg"
            className="input"
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
            }}
            minLength={5}
            maxLength={2000}
            rows={4}
          />
        </div>
      </fieldset>
      <p className="muted">Đề, phiên bản và câu hỏi được gắn tự động. Tối đa 5 báo lỗi mỗi giờ.</p>
      <Button
        disabled={busy || message.trim().length < 5}
        onClick={() => {
          void send();
        }}
      >
        {pending.current ? "Gửi lại báo lỗi" : "Gửi báo lỗi"}
      </Button>
    </Modal>
  );
}
